/**
 * Kontakt-Fachwerte – abgestimmt auf Backend LeadContactChannel / LeadContactDirection /
 * LeadContactResult / LeadPipelineKind. Für Vergleiche verwenden statt Magic Numbers.
 */
export const LEAD_CONTACT_CHANNEL_NONE = 0;
export const LEAD_CONTACT_CHANNEL_CALL = 1;
export const LEAD_CONTACT_CHANNEL_EMAIL = 2;
export const LEAD_CONTACT_CHANNEL_WHATSAPP = 3;
export const LEAD_CONTACT_CHANNEL_SMS = 4;
export const LEAD_CONTACT_CHANNEL_IN_PERSON = 5;

export const LEAD_CONTACT_DIRECTION_NONE = 0;
export const LEAD_CONTACT_DIRECTION_INBOUND = 1;
export const LEAD_CONTACT_DIRECTION_OUTBOUND = 2;

export const LEAD_CONTACT_RESULT_NONE = 0;
export const LEAD_CONTACT_RESULT_APPOINTMENT_BOOKED = 1;
export const LEAD_CONTACT_RESULT_TRAINING_AGREED = 2;
export const LEAD_CONTACT_RESULT_RETAINED = 3;
export const LEAD_CONTACT_RESULT_DECLINED = 4;
export const LEAD_CONTACT_RESULT_FOLLOW_UP = 5;

/** Vertrieb (Leads) oder Mitgliederbetreuung. */
export const LEAD_PIPELINE_KIND_SALES = 0;
export const LEAD_PIPELINE_KIND_MEMBER_CARE = 1;
export type LeadPipelineKind = typeof LEAD_PIPELINE_KIND_SALES | typeof LEAD_PIPELINE_KIND_MEMBER_CARE;

export type ContactBlockFlags = { doNotContactCalls?: boolean | null; doNotContactMessages?: boolean | null };

/** Nachrichtenkanäle unterliegen der Nachrichtensperre; Anruf der Anrufsperre; persönlich nie. */
export function isMessageChannel(channel: number): boolean {
  return channel === LEAD_CONTACT_CHANNEL_EMAIL || channel === LEAD_CONTACT_CHANNEL_WHATSAPP || channel === LEAD_CONTACT_CHANNEL_SMS;
}

/**
 * Spiegel der Backend-Regel PersonContactPolicy.Check — Bedienkomfort, die API-Prüfung ist die Regel.
 * Eingehend und persönlich sind immer erlaubt.
 */
export function contactBlockedBy(channel: number, direction: number, flags: ContactBlockFlags): "calls" | "messages" | null {
  if (direction !== LEAD_CONTACT_DIRECTION_OUTBOUND) return null;
  if (channel === LEAD_CONTACT_CHANNEL_CALL && flags.doNotContactCalls) return "calls";
  if (isMessageChannel(channel) && flags.doNotContactMessages) return "messages";
  return null;
}

/** Ergebnisse, die für die Pipeline-Art sinnvoll sind — Betreuung kennt „Training vereinbart" und „gehalten". */
export function contactResultsForKind(kind: LeadPipelineKind): number[] {
  return kind === LEAD_PIPELINE_KIND_MEMBER_CARE
    ? [LEAD_CONTACT_RESULT_TRAINING_AGREED, LEAD_CONTACT_RESULT_RETAINED, LEAD_CONTACT_RESULT_APPOINTMENT_BOOKED, LEAD_CONTACT_RESULT_FOLLOW_UP, LEAD_CONTACT_RESULT_DECLINED]
    : [LEAD_CONTACT_RESULT_APPOINTMENT_BOOKED, LEAD_CONTACT_RESULT_FOLLOW_UP, LEAD_CONTACT_RESULT_DECLINED];
}

export function contactResultLabelKey(result: number): string | null {
  switch (result) {
    case LEAD_CONTACT_RESULT_APPOINTMENT_BOOKED: return "Contact result appointment booked";
    case LEAD_CONTACT_RESULT_TRAINING_AGREED: return "Contact result training agreed";
    case LEAD_CONTACT_RESULT_RETAINED: return "Contact result retained";
    case LEAD_CONTACT_RESULT_DECLINED: return "Contact result declined";
    case LEAD_CONTACT_RESULT_FOLLOW_UP: return "Contact result follow-up";
    default: return null;
  }
}

export function contactChannelLabelKey(channel: number): string | null {
  switch (channel) {
    case LEAD_CONTACT_CHANNEL_CALL: return "Call";
    case LEAD_CONTACT_CHANNEL_EMAIL: return "Email";
    case LEAD_CONTACT_CHANNEL_WHATSAPP: return "WhatsApp";
    case LEAD_CONTACT_CHANNEL_SMS: return "SMS";
    case LEAD_CONTACT_CHANNEL_IN_PERSON: return "Contact channel in person";
    default: return null;
  }
}

/**
 * Formularwerte eines Kontakts — Vertrieb und Betreuung teilen sie. Der Kanal kommt vom Aufrufer
 * (Aktion „Anruf protokollieren“ / „persönlich“), Richtung, „erreicht“ und Ergebnis aus den Feldern.
 */
export type ContactLogValues = {
  channel: number;
  direction: number;
  /** Nur bei ausgehendem Anruf: Gegenüber erreicht. Eingehend ist immer erreicht. */
  reached: boolean;
  result: number;
};

export const CONTACT_LOG_DEFAULTS: ContactLogValues = {
  channel: LEAD_CONTACT_CHANNEL_CALL,
  direction: LEAD_CONTACT_DIRECTION_OUTBOUND,
  reached: true,
  result: LEAD_CONTACT_RESULT_NONE
};
